import React, { useReducer, useEffect, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import Fuse from 'fuse.js';
import { makeStyles } from '@material-ui/styles';
import { CaretUpOutlined } from '@ant-design/icons';
import { CheckOutlined, SearchOutlined } from '@ant-design/icons';

const useStyles = makeStyles(
  theme => ({
    searchWrap: {
      position: 'relative',
      '&:hover': {
        cursor: 'pointer',
      },
    },
    searchBox: {
      opacity: 1,
      backgroundColor: theme.colors.gray1,
      borderRadius: theme.borderRadius.min,
      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
      minWidth: '300px',
      position: 'absolute',
      zIndex: 3,
      marginTop: '4px',
    },
    searchHeader: {
      color: '#9E9E9E',
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
      textTransform: 'capitalize',
      ...theme.center,
    },
    filterSvg: {
      fontSize: '12px',
      color: '#9E9E9E',
      marginLeft: '8px',
    },
    svgRotate: {
      transform: 'rotateX(180deg)',
      transformOrigin: 'center',
    },
    inputContainer: {
      padding: '12px',
      position: 'relative',
      width: '100%',
    },
    searchInput: {
      border: '1px solid rgba(0,0,0,.15)',
      borderRadius: theme.borderRadius.min,
      color: '#4c4c4c',
      fontSize: '14px',
      height: '32px',
      lineHeight: '22px',
      margin: '0',
      outline: 0,
      padding: '5px 22px 5px 8px',
      width: '100%',
      appearance: 'none',
      '&::placeholder': {
        fontSize: '14px',
        color: 'rgba(0,0,0,.15)',
      },
    },
    searchIcon: {
      color: 'rgba(0,0,0,.25)',
      position: 'absolute',
      right: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'inline-block',
      fontStyle: 'normal',
      lineHeight: '0',
      textAlign: 'center',
      marginRight: '8px',
    },
    listBox: {
      listStyle: 'none',
      height: '192px',
      width: '100%',
      margin: 0,
      padding: 0,
      overflowY: 'auto',
    },
    optionlist: {
      cursor: 'pointer',
      fontSize: '14px',
      lineHeight: '24px',
      overflow: 'hidden',
      padding: '4px 12px',
      position: 'relative',
      textOverflow: 'ellipsis',
      fontWeight: 'normal',
      '&:hover': {
        backgroundColor: '#ecf8f7',
      },
    },
    defaultColor: { color: '#4c4c4c' },
    activeSelected: {
      color: theme.colors.mainGreen6,
      fontWeight: 500,
    },

    optionIcon: {
      color: '#38b2a6',
      position: 'absolute',
      left: '264px',
      top: '50%',
      transform: 'translateY(-50%)',
    },

    clear: {
      padding: '12px',
      ...theme.flex.mainSpaceBetween,
      '& button': {
        background: 'transparent',
        border: '0',
        color: theme.colors.mainGreen6,
        cursor: 'pointer',
        fontSize: '12px',
        lineHeight: '22px',
        margin: 0,
        outline: 'none',
        padding: 0,
        textTransform: 'capitalize',
      },
    },
    hideBox: {
      display: 'none',
    },
    showBox: {
      display: 'block',
    },
  }),
  { name: 'newSearch' },
);

const Search = props => {
  const wrapperRef = useRef(null);
  let { selectType, placeholder, submitType, onSearch, defaultChecked } = props;
  const [title, setTitle] = useState(placeholder);
  const [hideSearch, setHideSearch] = useState(true);
  const { t } = useTranslation();
  const initState = {
    options: [],
    initdata: [],
    inputDefaultValue: '',
    chooseOptions: [],
  };
  const clear = state => {
    const { options } = state;
    const optionOperate = cloneDeep(options);
    optionOperate?.map(option => Object.assign(option, { isSelected: false }));
    return { ...state, options: optionOperate, inputDefaultValue: '' };
  };
  const recoverState = state => {
    const { initdata, chooseOptions } = state;
    const newInitdata = cloneDeep(initdata);
    newInitdata.map(item => Object.assign(item, { isSelected: false }));
    if (chooseOptions.length) {
      chooseOptions.forEach(chooseOption => {
        let index = initdata.findIndex(item => chooseOption[submitType] === item[submitType]);
        newInitdata[index].isSelected = true;
      });
    }
    return { ...state, options: newInitdata, inputDefaultValue: '' };
  };
  const reducer = useCallback((state, action) => {
    switch (action.type) {
      case 'selected':
        return selected(state, action.index);
      case 'clear':
        return clear(state);
      case 'search':
        return search(state, action.filter);
      case 'init':
        return initData(state);
      case 'recover':
        return recoverState(state);
      case 'KeeySame':
        return keepSame(state, action.options, action.initdata, action.chooseOptions);
      default:
        throw new Error('Unknown action');
    }
  }, []);

  const keepSame = (state, options, initdata, chooseOptions) => {
    return { ...state, options, initdata, chooseOptions };
  };

  const classes = useStyles();

  const setSubNumber = state => {
    let { initdata } = state;
    let searchTitle = title;
    let selectArr = state?.options?.filter(option => option.isSelected === true);
    const moreTranslation = t('cms.landlords.filter_countries_account', {
      number: selectArr.length,
    });
    let initdataOperation = cloneDeep(initdata);

    if (selectArr?.length > 0) {
      initdata.forEach((item, initIndex) => {
        selectArr.forEach(select => {
          if (item[selectType] === select[selectType]) {
            initdataOperation[initIndex] = Object.assign({}, select);
          } else {
            initdataOperation[initIndex].isSelected = false;
          }
        });
      });
    }
    if (selectArr.length === 0) {
      initdataOperation.map(initData => Object.assign(initData, { isSelected: false }));
    }
    dispatch({
      type: 'KeeySame',
      options: state.options,
      initdata: initdataOperation,
      chooseOptions: cloneDeep(selectArr),
    });
    searchTitle =
      selectArr.length === 0
        ? placeholder
        : selectArr.length === 1
        ? selectArr[0][selectType]
        : moreTranslation;
    setTitle(searchTitle);
    if (selectArr.length) {
      selectArr = selectArr.map(selector => selector[submitType]);
    }
    onSearch(selectArr.length ? selectArr : []);
    setHideSearch(true);
  };
  const selected = (state, index) => {
    let options = cloneDeep(state.options);
    const isSelected = options[index]?.isSelected;
    options[index].isSelected = !isSelected;
    return { ...state, options };
  };

  const search = (state, filterValue) => {
    let options = cloneDeep(state.initdata);
    if (filterValue != '') {
      let fuseSearch = new Fuse(state.initdata, {
        keys: [selectType],
        threshold: 0.1,
      });
      options = fuseSearch?.search(filterValue)?.map(option => option.item);
    }
    options?.map(option => Object.assign(option, { isSelected: false }));
    return { ...state, options, inputDefaultValue: filterValue };
  };

  const initData = state => {
    let { optionLists } = props;

    optionLists?.map(option => Object.assign(option, { isSelected: false }));
    let checkedIndexs = [];
    let checkedArr = [];
    if (defaultChecked?.length > 0 && defaultChecked[0] !== '') {
      defaultChecked?.forEach(defalutCheck => {
        let index = optionLists.findIndex(optionList => optionList[submitType] === defalutCheck);
        checkedIndexs.push(index);
        checkedArr.push(optionLists[index]);
      });
      if (checkedIndexs.length === 0) {
        setTitle(placeholder);
      }
      if (checkedIndexs.length === 1) {
        setTitle(optionLists[checkedIndexs[0]][selectType]);
      }
      if (checkedIndexs.length > 1) {
        const moreTranslation = t('cms.landlords.filter_countries_account', {
          number: checkedIndexs.length,
        });
        setTitle(moreTranslation);
      }
      checkedIndexs.forEach(checkIndex => {
        optionLists[checkIndex].isSelected = true;
      });
    }
    return {
      ...state,
      initdata: cloneDeep(optionLists),
      options: cloneDeep(optionLists),
      chooseOptions: cloneDeep(checkedArr),
    };
  };

  const [state, dispatch] = useReducer(reducer, initState);

  useEffect(() => {
    dispatch({ type: 'init' });
  }, []);
  const handleClickOutside = event => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setHideSearch(true);
    }
  };
  useEffect(() => {
    document?.addEventListener('mousedown', handleClickOutside);
    return () => {
      document?.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);
  return (
    <div style={{ marginRight: '20px' }} className={classes.searchWrap} ref={wrapperRef}>
      <div
        className={classes.searchHeader}
        onClick={() => {
          setHideSearch(!hideSearch);
          dispatch({ type: 'recover' });
        }}
      >
        {title}

        <CaretUpOutlined
          className={`${classes.filterSvg} ${hideSearch ? classes.svgRotate : ''}`}
        />
      </div>
      <section
        className={`${classes.searchBox} ${hideSearch ? classes.hideBox : classes.showBox} `}
      >
        <div className={classes.inputContainer}>
          <input
            onChange={e => {
              dispatch({ type: 'search', filter: e?.target?.value });
            }}
            placeholder={t(
              'cms.global.search_component.placeholder.please_input_search_content_here',
            )}
            className={classes.searchInput}
            value={state.inputDefaultValue}
          />
          <SearchOutlined className={classes.searchIcon} />
        </div>
        <ul className={classes.listBox}>
          {state?.options?.map((option, index) => (
            <li
              onClick={() => {
                dispatch({ type: 'selected', index });
              }}
              key={`search-${index}`}
              className={`${classes.optionlist} ${
                option.isSelected ? classes.activeSelected : classes.defaultColor
              }`}
            >
              {option[selectType]}
              {option?.isSelected && <CheckOutlined className={classes.optionIcon} />}
            </li>
          ))}
        </ul>
        <footer className={classes.clear}>
          <button
            type="button"
            onClick={() => {
              dispatch({ type: 'clear' });
            }}
          >
            {t('cms.global.search_component.clear_selected')}
          </button>
          <button
            type="button"
            onClick={() => {
              setSubNumber(state);
            }}
          >
            {t('cms.switch_role_page.btn.confirm')}
          </button>
        </footer>
      </section>
    </div>
  );
};

Search.propTypes = {
  optionLists: PropTypes.array.isRequired,
  selectType: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  submitType: PropTypes.string,
  defaultChecked: PropTypes.array,
  placeholder: PropTypes.string.isRequired,
};
export default Search;
